import { useState } from 'react';

import { useIntl } from 'react-intl';
import { useUpdateEffect } from 'react-use';
import { isEqual } from 'lodash';

import { useAppSelector } from 'hooks';
import { FiltersPanel } from 'components/UI';
import { getTasksStatusOptions, getTasksTimeOptions } from 'utils/Task';
import { Dropdown } from 'components/Forms';
import { formatDropdownOptions } from 'utils/Object';
import {
  getAdminsByGid,
  getAllCarePrograms,
  getCurrentGroup,
  getCurrentOrgSubgroups,
  getCurrentTagsByGroup,
  getTimeLogTypes,
  selectCurrentGid
} from 'store/groups/reducer';
import { getGroupsAdmins, isOrganization } from 'utils/Groups';

import {
  AssignUsers,
  CarePrograms,
  ClinicalTimeLog,
  PatientStatus,
  Subgroups,
  Tags
} from '../Filters';

interface ITasksFilters {
  filters: ITemplatePageFilters[];
  setFilters: (filters: ITemplatePageFilters[]) => void;
}

const TasksFilters = ({ filters, setFilters }: ITasksFilters) => {
  const { formatMessage } = useIntl();
  const subgroups = useAppSelector(getCurrentOrgSubgroups) || [];
  const currentGroup = useAppSelector(getCurrentGroup);
  const gid = useAppSelector(selectCurrentGid);
  const groupUsers = useAppSelector((state) => getAdminsByGid(state, gid)) || [];
  const tagsByGroup = useAppSelector(getCurrentTagsByGroup) || [];
  const dataLogTypes = useAppSelector(getTimeLogTypes) || [];
  const carePrograms = useAppSelector(getAllCarePrograms);
  const subgroupsFilterOptions = currentGroup && isOrganization(currentGroup) ? subgroups : [];
  const [selectedFilters, setSelectedFilters] = useState<ITemplatePageFilters[]>([...filters]);
  const timeLogMinValue =
    selectedFilters.find((filter) => filter.filterLabel === 'timeLogMin')?.filterValue?.[0] || '';
  const timeLogMaxValue =
    selectedFilters.find((filter) => filter.filterLabel === 'timeLogMax')?.filterValue?.[0] || '';
  const clinicalTimeLogMinUpperMax = parseInt(timeLogMinValue) > parseInt(timeLogMaxValue);
  const clinicalTimeLogMinNegative = parseInt(timeLogMinValue) < 0;
  const clinicalTimeLogMaxNegative = parseInt(timeLogMaxValue) < 0;
  const careProgramsTranslated = carePrograms.map((program) => ({
    ...program,
    label: formatMessage({ id: `${program.name}_short` })
  }));

  const handleApply = () => {
    setFilters(selectedFilters);
  };

  const clearSelected = () => {
    setFilters([]);
    setSelectedFilters([]);
  };

  const handleChange = (type: IFilterLabel, data?: ISelectOption[], value?: string) => {
    const oldFilters: ITemplatePageFilters[] = [...selectedFilters].filter(
      (filter) => filter.filterLabel !== type
    );
    const newFilters: ITemplatePageFilters[] = [...oldFilters];
    if ((data && data.length > 0) || value) {
      const newTypeFilters: ITemplatePageFilters = {
        filterLabel: type,
        ...(data && { filterData: data, filterValue: data?.map((x) => x.value) }),
        ...(value && { filterValue: [value] })
      };
      newFilters.push(newTypeFilters);
    }
    setSelectedFilters(newFilters);
  };

  const getFiltersDataByLabel = (data: ITemplatePageFilters[], label: IFilterLabel) => {
    return data.find((filter) => filter.filterLabel === label)?.filterData || [];
  };

  useUpdateEffect(() => {
    setSelectedFilters(filters);
  }, [filters]);

  return (
    <FiltersPanel
      onApply={handleApply}
      onClear={clearSelected}
      disableApply={isEqual(selectedFilters, filters)}
      onClose={() => {
        if (selectedFilters !== filters) setSelectedFilters(filters);
      }}
      clearable={!!selectedFilters.length}
      hasMargins={false}
      filtersCount={selectedFilters.length}
    >
      <>
        {subgroupsFilterOptions.length >= 1 && (
          <>
            <Subgroups
              options={formatDropdownOptions(subgroupsFilterOptions, 'gid', 'name')}
              value={getFiltersDataByLabel(selectedFilters, 'subgroups')}
              handleChange={(data: ISelectOption[]) => handleChange('subgroups', data)}
            />
            <hr />
          </>
        )}
        <Dropdown
          dataTestId="filterTaskTimeStatus"
          variant="form"
          options={getTasksTimeOptions()}
          onChange={(v: any) => handleChange('taskTime', v ? [v] : undefined)}
          isClearable
          value={getFiltersDataByLabel(selectedFilters, 'taskTime')}
          label={formatMessage({ id: 'timeKey' })}
        />
        <hr />
        <AssignUsers
          options={formatDropdownOptions(
            subgroups.length > 0 &&
              ((currentGroup && isOrganization(currentGroup)) || gid === 'all')
              ? getGroupsAdmins(subgroups.map((g) => g.gid))
              : groupUsers,
            'aid',
            'name'
          )}
          value={getFiltersDataByLabel(selectedFilters, 'assignUsers')}
          handleChange={(data: ISelectOption[]) => handleChange('assignUsers', data)}
        />
        <hr />
        <Dropdown
          dataTestId="filterTaskStatus"
          variant="form"
          options={getTasksStatusOptions()}
          onChange={(data: ISelectOption[]) => handleChange('taskStatus', data)}
          isMulti
          isClearable={false}
          value={getFiltersDataByLabel(selectedFilters, 'taskStatus')}
          label={formatMessage({ id: 'taskStatusKey' })}
        />
        <hr />
        <Tags
          options={formatDropdownOptions(tagsByGroup, 'tagId', 'tagLabel', {}, 'tagSvg')}
          value={getFiltersDataByLabel(selectedFilters, 'tags')}
          handleChange={(data: ISelectOption[]) => handleChange('tags', data)}
        />
        <hr />
        <PatientStatus
          value={getFiltersDataByLabel(selectedFilters, 'patientStatuses')}
          handleChange={(data: ISelectOption[]) => handleChange('patientStatuses', data)}
        />
        <hr />
        <CarePrograms
          options={formatDropdownOptions(careProgramsTranslated, 'cpid', 'label')}
          value={getFiltersDataByLabel(selectedFilters, 'patientCareProgram')}
          handleChange={(data: ISelectOption[]) => handleChange('patientCareProgram', data)}
        />
        <hr />
        <ClinicalTimeLog
          timeLogTypeOptions={formatDropdownOptions(
            dataLogTypes.map((logType) => ({
              ...logType,
              label: formatMessage({ id: logType.labelKey })
            })),
            'labelKey',
            'label'
          )}
          timeLogTypeValue={getFiltersDataByLabel(selectedFilters, 'timeLogType')}
          timeLogMinValue={timeLogMinValue}
          timeLogMaxValue={timeLogMaxValue}
          handleChange={handleChange}
          minUpperMax={clinicalTimeLogMinUpperMax}
          minNegative={clinicalTimeLogMinNegative}
          maxNegative={clinicalTimeLogMaxNegative}
        />
      </>
    </FiltersPanel>
  );
};

export default TasksFilters;
